import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Cookies from 'js-cookie';
import { useRouter } from 'next/dist/client/router';
import { useDispatch, useSelector } from 'react-redux';

import {
  GAEvent,
  getGASelectItem,
  getGACartItemsParams,
  getGAViewItemDetail,
  getGASelectPromotionDetail,
} from 'utils/gtag';
import { IState } from 'store';
import api from 'services/api';
import { useNotices } from 'hooks';
import {
  createDebouncedDecrementHandler,
  createIncrementHandler,
} from 'utils/itemCounterFunctions';
import { imageZoom } from 'utils/zoomImage';
import { formatCurrency } from 'utils/format';
import { objectIsEmpty } from 'utils/objectEmpty';
import { IProductProps } from 'store/modules/product';
import { addProductToCart } from 'store/modules/cart';
import { IConfigApplicationProps, IConfigProps } from 'store/modules/config';
import { cardsPrimary, cardsSecondary, IProductSelosProps } from 'utils/cards';
import { IUserProps, removeFavorite, setFavorite } from 'store/modules/profile';

import Button from 'components/common/Button';
import { Gift } from 'components/pages/PDP/Gift';
import CardsList from 'components/common/CardsList';
import { Rating } from 'components/pages/PDP/Rating';
// import { Freight } from 'components/pages/PDP/Freight';
import { Gallery } from 'components/pages/PDP/Gallery';
import ItemCounter from 'components/pages/PDP/ItemCounter';
import SOComponent from 'components/pages/PDP/SOComponent';
import { Correlatos } from 'components/pages/PDP/Correlatos';
import { SendMeEmail } from 'components/pages/PDP/SendMeEmail';
import { ModalGetGift } from 'components/pages/PDP/ModalGetGift';
import { ProductSelos } from 'components/pages/PDP/ProductSelos';
import { ModalSendRecipe } from 'components/pages/PDP/ModalSendRecipe';
import { AboutFreeFreight } from 'components/pages/PDP/AboutFreeFreight';
import { ExclusiveProduct } from 'components/pages/PDP/ExclusiveProduct';
import { ProgressiveDiscount } from 'components/pages/PDP/ProgressiveDiscount';
import { ModalCopyToClipboard } from 'components/pages/PDP/ModalCopyToClipboard';
import { ProductImagesCarousel } from 'components/pages/PDP/ProductImagesCarousel';
// import { DescriptionTabPagination } from 'components/pages/PDP/DescriptionTabPagination';
import { ProgressiveItems } from 'components/pages/PDP/ProgressiveDiscount/ProgressiveItems';

import * as S from './styles';

interface IProductItemProps {
  product: IProductProps;
}

export function Product({ product }: IProductItemProps) {
  const router = useRouter();
  const dispatch = useDispatch();
  const timeoutRef = useRef(null);
  const { addNotices } = useNotices();

  const { config } = useSelector<IState, IConfigProps>(state => state.config);
  const user = useSelector<IState, IUserProps>(state => state.profile.user);
  const { ultraDescontao } = useSelector<IState, IConfigApplicationProps>(
    state => state.config.config,
  );

  const [quantityItems, setQuantityItems] = useState(0);
  const [showModalGetGift, setShowModalGetGift] = useState(false);
  const [isFavorite, setIsFavorite] = useState(product.ehFavorito);
  const [showModalLetMeKnow, setShowModalLetMeKnow] = useState(false);
  const [showModalSendRecipe, setShowModalSendRecipe] = useState(false);
  const [showModalAboutFreeFreight, setShowModalAboutFreeFreight] =
    useState(false);
  const [showModalCopyToClipboard, setShowModalCopyToClipboard] =
    useState(false);
  const [componenteVitrineId, setComponenteVitrineId] = useState<number | null>(
    null,
  );

  const productSelos: IProductSelosProps = useMemo(() => {
    const descontoProgressivo =
      product.promocaoProgressiva && product.promocaoProgressiva.id === 38;

    const importado = product.importado
      ? `importado${product.seloImportado}`
      : null;

    return {
      freteGratis: product.freteGratis,
      ultraDesconto: product.ultraDesconto && ultraDescontao.estaVigente,
      descontoProgressivo: descontoProgressivo,
      possuiGenerico: product.possuiGenerico,
      bloqueadoVendaSite: product.bloqueadoVendaSite,
      possuiBrinde: product.possuiBrinde,
      ultraCompare: product.ultraCompare,
      // importado: importado,
      // ultraPremio: product.ultraPremio,
      seloLivre: product.seloLivre,
      cliqueRetire: product.cliqueRetire,
    };
  }, [product, ultraDescontao]);

  const productSelosPrimary = cardsPrimary;
  const productSelosSecondary = cardsSecondary;

  Object.keys(productSelosPrimary).forEach(key => {
    if (productSelos.hasOwnProperty(key)) {
      cardsPrimary[key] = productSelos[key];
    }
  });
  Object.keys(productSelosSecondary).forEach(key => {
    if (productSelos.hasOwnProperty(key)) {
      cardsSecondary[key] = productSelos[key];
    }
  });

  useEffect(() => {
    if (!product.imagemPrincipal) {
      router.back();
    }
  }, [product, router]);

  useEffect(() => {
    const campanha = Cookies.get('@SportsNutrition:campanha');

    if (campanha) {
      GAEvent({
        action: 'select_promotion',
        data: getGASelectPromotionDetail({
          campanha: JSON.parse(campanha),
          product,
        }),
      });
    } else {
      const itemList = Cookies.get('@SportsNutrition:itemList');

      if (itemList) {
        GAEvent({
          action: 'view_item',
          data: getGAViewItemDetail({ product, itemListName: itemList }),
        });

        GAEvent({
          action: 'select_item',
          data: getGASelectItem({
            product,
            itemListId: itemList,
            itemListName: itemList,
          }),
        });
        Cookies.remove('@SportsNutrition:itemList');
      }
    }
  }, []);

  useEffect(() => {
    const { cvi } = router.query;

    if (cvi) {
      setComponenteVitrineId(Number(cvi));
      delete router.query.cvi;
      router.replace(router);
    }
  }, [router]);

  useEffect(() => {
    const verifyFavorite = async () => {
      try {
        const response = await api.get(`/favoritos/${product.id}/verificar`);

        setIsFavorite(response.data);
      } catch (err) {}
    };

    verifyFavorite();
  }, []);

  function redirectToReviews() {
    document.getElementById('reviews').scrollIntoView({ behavior: 'smooth' });
  }

  const handleFavoriteProduct = useCallback(() => {
    if (!user || objectIsEmpty(user)) {
      router.push(`/identificacao?ReturnUrl=/${product.permalink}`);
    } else if (isFavorite) {
      dispatch(
        removeFavorite({ productId: product.id, sku: product.permalink }),
      );

      setIsFavorite(false);

      addNotices({
        type: 'success',
        messages: [`${product.nome} foi removido dos seus favoritos!`],
      });
    } else {
      dispatch(
        setFavorite({
          clientId: user.id,
          productId: product.id,
          sku: product.permalink,
        }),
      );

      setIsFavorite(true);

      addNotices({
        type: 'success',
        messages: [`${product.nome} foi salvo nos seus favoritos!`],
      });
    }
  }, [product, isFavorite]);

  const handleLetMeKnow = useCallback(async () => {
    try {
      const response = await api.post('aviseme', {
        email: user.email,
        sku: product.sku,
        nome: user.nomeCompleto,
      });

      if (!response.data.contemAvisos && !response.data.contemErros) {
        setShowModalLetMeKnow(false);

        addNotices({
          type: 'success',
          messages: [
            'Registramos sua solicitação, assim que o produto voltar ao estoque entraremos em contato!',
          ],
        });
      }
    } catch (err) {
      if (err?.data) {
        addNotices({ messages: [err.data.erros], type: 'errors' });
      }
    }
  }, [user]);

  const handleAddProduct = useCallback(
    (showAfterRequest: boolean, quantity?: number) => {
      dispatch(
        addProductToCart({
          sku: product.codigo,
          quantity: quantity ? quantity : quantityItems,
          showAfterRequest,
          GA: getGACartItemsParams({
            product,
            qty: quantity ? quantity : quantityItems,
          }),
          componenteVitrineId,
        }),
      );
    },
    [componenteVitrineId, dispatch, product, quantityItems],
  );

  const handleAddProductAndPush = useCallback(() => {
    handleAddProduct(false);

    router.push('/checkout/carrinho');
  }, [componenteVitrineId, dispatch, product, quantityItems, router]);

  const handleDecrementItem = useMemo(
    () =>
      createDebouncedDecrementHandler({
        quantity: quantityItems,
        item: product,
        dispatch,
        timeoutRef,
      }),
    [quantityItems, product, dispatch],
  );

  const handleIncrementItem = useMemo(
    () =>
      createIncrementHandler({
        item: product,
        dispatch,
      }),
    [product, dispatch],
  );

  return (
    <>
      <S.Container className="product">
        <h2 className="product-title">{product.nome}</h2>

        <div className="product-container">
          <div className="product-references-images-tags-cards">
            <div className="product-references-rating-actions">
              <div className="product-references">
                <div>
                  {/* Garantia de qualidade: */}
                  <a
                    href={`/marca/${product.marca.permalink}`}
                    className="brand-link"
                  >
                    {product.marca.nome}
                  </a>
                </div>
                <div>
                  Código:
                  <strong>{product.codigo}</strong>
                </div>
              </div>

              <div
                className="product-rating"
                onClick={() => redirectToReviews()}
              >
                <Rating
                  nota={
                    product.resumoAvaliacao ? product.resumoAvaliacao.media : 0
                  }
                />
                <span className="product-number-reviews">
                  ({product.resumoAvaliacao ? product.resumoAvaliacao.total : 0}
                  )
                </span>
              </div>

              <div className="product-actions">
                <img
                  src="/images/icons/share.svg"
                  alt="Compartilhar link"
                  className="icon share-icon"
                  onClick={() => setShowModalCopyToClipboard(true)}
                />

                {isFavorite ? (
                  <img
                    src="/images/icons/heart-fill.svg"
                    alt="Coração"
                    className="icon heart-fill-icon"
                    onClick={handleFavoriteProduct}
                  />
                ) : (
                  <img
                    src="/images/icons/heart.svg"
                    alt="Coração"
                    className="icon heart-icon"
                    onClick={handleFavoriteProduct}
                  />
                )}
              </div>
            </div>

            {(product.novidade ||
              product.freteGratis ||
              product.possuiBrinde ||
              Object.values(productSelosPrimary).some(valor =>
                Boolean(valor),
              )) && (
              <div className="product-details mobile">
                <ProductSelos
                  product={product}
                  productSelosPrimary={productSelosPrimary}
                  setShowModalAboutFreeFreight={setShowModalAboutFreeFreight}
                />
              </div>
            )}

            <div className="product-images">
              <Gallery
                images={product.imagens}
                // classNameContainer={
                //     product.correlatados.length > 0
                //         ? ' have-correlatos'
                //         : ''
                // }
              />

              <div className="principal-image">
                {product.ultraDesconto && ultraDescontao.estaVigente && (
                  <img
                    src={ultraDescontao?.seloVigenteVitrineUrl}
                    className="Selo Ultradesconto"
                  />
                )}

                {product.seloCIMED && (
                  <img
                    src="/images/card-icons/selo_cimed.svg"
                    className="Selo CIMED"
                  />
                )}

                {product.freteGratis && (
                  <img
                    src="/images/card-icons/frete-gratis-tag.svg"
                    alt="Frete Grátis"
                    className="Selo frete-gratis"
                    onClick={() => setShowModalAboutFreeFreight(true)}
                  />
                )}

                <CardsList
                  hovered={true}
                  productSelos={productSelosSecondary}
                  animationSelo="only-icon"
                />

                <div className="img-zoom-container">
                  <img
                    id="product-image"
                    className="product-image"
                    loading="lazy"
                    src={product.imagemPrincipal.grande}
                    alt={product.nome}
                    onLoad={() =>
                      imageZoom('product-image', 'resul-zoom-product')
                    }
                  />

                  <div id="resul-zoom-product" className="img-zoom-result" />
                </div>

                {product.promocaoProgressiva !== null &&
                  product.promocaoProgressiva.imagemPDPUrl && (
                    <img
                      src={product.promocaoProgressiva.imagemPDPUrl}
                      alt="Selo promoção"
                      className="promotion-selo web"
                    />
                  )}
              </div>
            </div>

            <ProductImagesCarousel
              productName={product.nome}
              images={product.imagens}
              productSelosSecondary={productSelosSecondary}
              seloCimed={product.seloCIMED}
              ultraDesconto={
                product.ultraDesconto && ultraDescontao.esquentaEstaVigente
              }
              freteGratis={product.freteGratis}
              setShowModalAboutFreeFreight={setShowModalAboutFreeFreight}
            />

            {product.promocaoProgressiva !== null &&
              product.promocaoProgressiva.imagemPDPUrl && (
                <img
                  src={product.promocaoProgressiva.imagemPDPUrl}
                  alt="Selo promoção"
                  className="promotion-selo mobile"
                />
              )}
          </div>

          <div className="product-details">
            {/* {(product.novidade ||
              product.freteGratis ||
              product.possuiBrinde ||
              Object.values(productSelosPrimary).some(valor =>
                Boolean(valor),
              )) && (
              <ProductSelos
                product={product}
                productSelosPrimary={productSelosPrimary}
                setShowModalAboutFreeFreight={setShowModalAboutFreeFreight}
              />
            )} */}

            <div className="product-details-sold-freight-gift">
              <div className="product-sold-by-company">
                <span>Vendido e entregue por:</span>
                {product.farmacia.medicamento ? (
                  <img
                    loading="lazy"
                    src="/images/ultrafarma-logo-blue.svg"
                    alt="Ultrafarma"
                  />
                ) : (
                  <img
                    loading="lazy"
                    src={
                      config.aparencia?.logo
                        ? config.aparencia.logo
                        : '/images/ultrafarma-sports-nutrition-logo.png'
                    }
                    alt="Ultrafarma Sports Nutrition"
                  />
                )}
              </div>

              {product.disponivel ? (
                <span className="product-stock available">Em estoque</span>
              ) : (
                <span className="product-stock not-available">
                  Indisponível
                </span>
              )}
            </div>

            <div className="product-price-info">
              <div className="product-old-price-info">
                <p className="subtitle">De</p>

                <s>R$ {formatCurrency(product.preco.precoDe)}</s>
                {/* <span className="product-x-price-old"></span> */}

                <div>
                  <div className="tag-discount">
                    <span>{product.preco.porcentagemDesconto}% OFF</span>
                  </div>

                  <div className="so-club-cashback">
                    <span>*Cashback</span>
                    <span>{`R$ ${formatCurrency(
                      product.creditoBonificacaoClube,
                    )}`}</span>
                  </div>
                </div>
              </div>

              {(!!product.preco.precoPix ||
                product.parcelamento.totalParcelas !== 1) && (
                <div className="product-price-parcel">
                  {!!product.precoDeTexto && <p className="subtitle">Por</p>}

                  <div>
                    <span>{product.precoPorTexto}</span>

                    {product.parcelamento.totalParcelas > 1 && (
                      // && product.promocaoProgressiva === null ||
                      // !product.promocaoProgressiva
                      //   .exibirPrecoUnitarioPromocaoVitrine ||
                      // product.variacaoPadrao.parcelamentoPromocao === null ||
                      // product.variacaoPadrao.parcelamentoPromocao.totalParcelas <=
                      //   1
                      <p>
                        {product.parcelamento.totalParcelas !== 1 && (
                          <>
                            {' '}
                            em {product.parcelamento.totalParcelas || 1}x{'  '}
                            <b>
                              {
                                product.parcelamento.parcelas[
                                  product.parcelamento.parcelas.length - 1
                                ].valorTexto
                              }
                            </b>{' '}
                            sem juros
                          </>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {product.promocaoProgressiva !== null &&
                product.promocaoProgressiva
                  .exibirPrecoUnitarioPromocaoVitrine &&
                product.promocaoProgressiva.descricao && (
                  <div className="product-price-unity-text">
                    {product.promocaoProgressiva.descricao}
                  </div>
                )}

              {product.promocaoProgressiva !== null &&
                product.promocaoProgressiva
                  .exibirPrecoUnitarioPromocaoVitrine && (
                  <div
                    className="product-price-parcel"
                    data-fit-group="precos-sync"
                  >
                    <span>
                      {product.promocaoProgressiva.precoUnitarioPromocaoTexto}
                    </span>

                    {/* {product.variacaoPadrao.parcelamentoPromocao !== null &&
                      product.variacaoPadrao.parcelamentoPromocao
                        .totalParcelas > 1 && (
                        <p>
                          {' '}
                          em{' '}
                          {product.variacaoPadrao.parcelamentoPromocao
                            .totalParcelas || 1}
                          x{' '}
                          <b>
                            {
                              product.variacaoPadrao.parcelamentoPromocao
                                .ultimaParcela.valorTexto
                            }
                          </b>{' '}
                          sem juros
                        </p>
                      )} */}
                  </div>
                )}

              {product.preco.precoPix ? (
                <div className="product-price-pix">
                  <p className="subtitle">Pix</p>

                  <b>{product.precoPixTexto}</b>
                </div>
              ) : (
                <div className="product-price-for">
                  {!!product.precoDeTexto && <p className="subtitle">Por</p>}

                  <span>{product.precoPorTexto}</span>
                </div>
              )}

              {!!product.descontoPagamento && (
                <span className="product-discount-parcel">
                  ({product.descontoPagamento})
                </span>
              )}

              {/*
                  <div className="product-price-for-info">
                    <div>
                      <span className="product-price-for">
                        {product.precoPorTexto}
                      </span>
                    </div>

                    {product.parcelamento.totalParcelas &&
                      product.parcelamento.totalParcelas !== 1 && (
                        <span className="product-parcel">
                          Em até {product.parcelamento.totalParcelas || 1}x sem
                          juros no cartão de crédito!
                        </span>
                      )}

                    {!!product.descontoPagamento && (
                      <span className="product-payment-discount">
                        {product.descontoPagamento}
                      </span>
                    )}
                  </div> */}
            </div>

            {/* {product.enviarReceita && (
                  <div className="send-recipe">
                    <Button onClick={() => setShowModalSendRecipe(true)}>
                      Envie sua receita médica
                    </Button>
                  </div>
                )} */}

            {!product.bloqueadoVendaSite ? (
              <>
                {product.promocaoProgressiva &&
                  product.promocoesProgressivasRegras &&
                  product.promocoesProgressivasRegras.length > 0 && (
                    <>
                      {product.promocaoProgressiva.id === 38 ? (
                        <ProgressiveDiscount
                          promocaoCores={product.promocaoProgressiva.cores}
                          regras={product.promocoesProgressivasRegras}
                          onClick={handleAddProduct}
                        />
                      ) : (
                        <ProgressiveItems
                          promocaoCores={product.promocaoProgressiva.cores}
                          regras={product.promocoesProgressivasRegras}
                          onClick={handleAddProduct}
                        />
                      )}
                    </>
                  )}

                {product.possuiBrinde && !!product.brinde && (
                  <Gift
                    brinde={product.brinde}
                    onClick={() => {
                      setShowModalGetGift(true);
                      handleAddProduct(false, product.brinde.quantidade);
                    }}
                  />
                )}

                {/* CRIADO COMPONENTE DE VARIAÇÕES DO PRODUTO PARA CASO FUTURAMENTE PRECISAR */}
                {/* {product.variacoes && product.variacoes.length > 0 && (
                      <VariationList variacoes={product.variacoes} />
                    )} */}

                {product.disponivel && (
                  <div className="product-actions-buttons">
                    <ItemCounter
                      quantityMax={product.quantidadeMaximaVenda}
                      quantityMin={product.quantidadeMinimaVenda}
                      quantity={quantityItems}
                      setQuantity={setQuantityItems}
                      handleIncrementItem={handleIncrementItem}
                      handleDecrementItem={handleDecrementItem}
                    />

                    <Button onClick={handleAddProductAndPush}>Comprar</Button>

                    {/* <Button
                        className="button-component-outline"
                        onClick={() => handleAddProduct(true)}
                      >
                        <img
                          src="/images/icons/basket-black.svg"
                          alt="Adicionar ao carrinho"
                          className="icon shopping-blue-icon"
                        />
                        <img
                          src="/images/icons/basket.svg"
                          alt="Adicionar ao carrinho"
                          className="icon shopping-icon"
                        />
                        Adicionar ao carrinho
                      </Button> */}
                  </div>
                )}
              </>
            ) : (
              <ExclusiveProduct product={product} />
            )}

            {!product.disponivel && (
              <div className="product-not-available">
                <span>Produto indisponível</span>

                <Button
                  onClick={() => {
                    if (!!user && !objectIsEmpty(user)) {
                      handleLetMeKnow();
                    } else {
                      setShowModalLetMeKnow(true);
                    }
                  }}
                >
                  Avise-me
                </Button>
              </div>
            )}

            {/* {(!!product.descricaoGeral ||
              !!product.descricaoAplicacao ||
              !!product.descricaoAdicional) && (
              <DescriptionTabPagination
                descricao={product.descricaoGeral}
                modoDeUso={product.descricaoAplicacao}
                outrasInformacoes={product.descricaoAdicional}
              />
            )} */}

            {/* A ULTRAFARMA NÃO QUER QUE EXIBE O COMPONENTE DE FRETE NA PDP */}
            {/* {product.disponivel && <Freight quantityItems={quantityItems} />} */}

            <SOComponent product={product} />

            {/* TODO CRIAR TELA DE GENERICOS E SIMILARES, PARA CASO FUTURAMENTE PRECISAR, JÁ ESTIVER PRONTO */}
            {/*{product.possuiGenerico && (
              <Link href={`/genericos-e-similares/${product.id}`}>
                <a className="generic-and-similar">
                  Ver mais Genéricos e Similares
                </a>
              </Link>
            )} */}

            {!!product.correlatados && product.correlatados.length > 0 && (
              <Correlatos correlatados={product.correlatados} />
            )}
          </div>
        </div>
      </S.Container>

      {showModalLetMeKnow && (
        <SendMeEmail
          setShowModalLetMeKnow={setShowModalLetMeKnow}
          sku={product.sku}
        />
      )}

      {showModalCopyToClipboard && (
        <ModalCopyToClipboard
          setShowModalCopyToClipboard={setShowModalCopyToClipboard}
        />
      )}

      {showModalGetGift && (
        <ModalGetGift
          setShowModalGetGift={setShowModalGetGift}
          brinde={product.brinde}
        />
      )}

      {showModalAboutFreeFreight && (
        <AboutFreeFreight
          setShowModalFreeFreight={setShowModalAboutFreeFreight}
        />
      )}

      {showModalSendRecipe && (
        <ModalSendRecipe setShowModalSendRecipe={setShowModalSendRecipe} />
      )}
    </>
  );
}
